import { Switch, Route, Redirect, useLocation, Link } from "wouter";
import { useEffect } from "react";
import { posthog } from "@/lib/posthog";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/components/theme-provider";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/app-sidebar";
import { useAuth } from "@/hooks/use-auth";
import { RoleContext, useRole } from "@/hooks/use-role";
import { deriveRoleValue, shouldRedirectFromAdminRoute } from "@/lib/role-helpers";
import { traineeRedirectTarget } from "@/lib/agent-ui-helpers";
import QaList from "@/pages/qa-list";
import PrescreenList from "@/pages/prescreen-list";
import PrescreenDetail from "@/pages/prescreen-detail";
import ActiveTaskList from "@/pages/activetask-list";
import ActiveTaskDetail from "@/pages/activetask-detail";
import { EarlyRiskNotifier } from "@/components/early-risk-notifier";
import { OverrideNotificationHandler } from "@/components/override-notification-handler";
import QaDetail from "@/pages/qa-detail";
// SPEC-UnifiedQADetail Phase 2 — agent list survives at /qa/agent; the agent
// detail page is unified onto the senior `/qa/delivery/:id` route (one
// component, viewMode-aware). `agent-detail` is deleted; legacy bookmarks
// hit a one-cycle <Redirect> in the Router.
import AgentList from "@/pages/agent-list";
import { AgentClaimSubscriptionProvider } from "@/components/qa/agent-claim-subscription-provider";
import { ViewModeToggle } from "@/components/view-mode-toggle";
import { useViewMode } from "@/hooks/use-view-mode";
import AdminUsers from "@/pages/admin-users";
import AdminSettings from "@/pages/admin-settings";
import NotificationSettings from "@/pages/notification-settings";
import LandingPage from "@/pages/landing";
import AnnotationReview from "@/pages/annotation-review";
import ModelAudit from "@/pages/model-audit";
import BaselineManager from "@/pages/baseline-manager";
// SPEC-TrainingSimulator Phase A — Senior/Admin training-set management.
import TrainManage from "@/pages/train-manage";
import TrainManageItem from "@/pages/train-manage-item";
// SPEC-TrainingSimulator Phase B — the trainee runner.
import TrainHome from "@/pages/train-home";
import TrainRun from "@/pages/train-run";
import TrainLesson from "@/pages/train-lesson";
import NotFound from "@/pages/not-found";
// SPEC-AdminImpersonation §"Client wiring" — banner mounts above the existing
// app shell, error listener subscribes to the global MutationCache for
// read-only-mode toasts. Launcher lives on the admin settings page.
import { ImpersonationBanner } from "@/components/admin/impersonation-banner";
import { ImpersonationErrorListener } from "@/components/admin/impersonation-error-listener";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertCircle, Loader2 } from "lucide-react";
import { Starling } from "@/lib/starling-brand";

/**
 * SPEC-UnifiedQADetail Phase 2 — header lockup with viewMode-aware target.
 *
 * Click target follows the user's view-mode preference:
 *   - agent view  → /qa/agent (the agent queue)
 *   - senior view → /qa/list  (the senior list)
 *
 * Real agents are pinned to agent view by `useViewMode`, so their lockup
 * always points to /qa/agent.
 */
function HeaderLockup() {
  const { viewMode } = useViewMode();
  const { role } = useRole();
  // SPEC-TrainingSimulator §3 — a trainee's home is /train.
  const target = role === "trainee" ? "/train" : viewMode === "agent" ? "/qa/agent" : "/qa/list";
  return (
    <Link
      href={target}
      data-testid="header-starling-link"
      aria-label="Home"
      style={{ display: "inline-flex", textDecoration: "none" }}
    >
      <img
        src={Starling.lockupSrc}
        alt="Starling"
        data-testid="header-starling-lockup"
        style={{ height: 22, width: "auto", display: "block" }}
      />
    </Link>
  );
}

function AdminRoute({ component: Component }: { component: React.ComponentType }) {
  const { isAdmin } = useRole();
  if (!isAdmin) {
    return <Redirect to="/" />;
  }
  return <Component />;
}

/**
 * SPEC-TrainingSimulator §3 — /train/manage is Senior-or-Admin. Same
 * `role === null` loading guard as QaListRoute so an impersonation swap never
 * bounces a legitimate senior mid-resolve.
 */
function SeniorOrAdminRoute({ component: Component }: { component: React.ComponentType }) {
  const { role, isSenior, isAdmin } = useRole();
  if (role === null) return <LoadingScreen />;
  if (!isSenior && !isAdmin) return <Redirect to="/" />;
  return <Component />;
}

/**
 * SPEC-TrainingSimulator §3 / §10 guard `T` — /train is for trainees; Senior
 * and Admin may open it in preview (the runner flags it). Agents and bots are
 * bounced home. Same `role === null` loading guard as the routes above.
 */
function TraineeRoute({ component: Component }: { component: React.ComponentType }) {
  const { role, isSenior, isAdmin } = useRole();
  if (role === null) return <LoadingScreen />;
  if (role !== "trainee" && !isSenior && !isAdmin) return <Redirect to="/" />;
  return <Component />;
}

/**
 * Delivery State Model §5.1 — Agents cannot cherry-pick from the senior list.
 *
 * `/qa/agent` already guards non-agents OUT (see agent-list.tsx). This is the
 * missing inverse: an `agent`-role user who lands on `/` or `/qa/list` (the
 * senior cherry-pick list) is bounced to their own agent home at `/qa/agent`.
 * Without it, agents land on the senior list after login and can only reach
 * the agent home via a detail page's back-link.
 *
 * Same `role === null` loading guard as agent-list.tsx — never redirect while
 * the auth payload is still resolving (the impersonation cache-staleness race
 * documented in agent-list.tsx / impersonation.ts).
 */
function QaListRoute() {
  const { role } = useRole();
  if (role === null) return <LoadingScreen />;
  if (role === "agent") return <Redirect to="/qa/agent" />;
  if (role === "trainee") return <Redirect to="/train" />;
  return <QaList />;
}

function Router() {
  return (
    <Switch>
      <Route path="/" component={QaListRoute} />
      <Route path="/qa/list" component={QaListRoute} />
      <Route path="/qa/prescreening" component={PrescreenList} />
      <Route path="/qa/prescreen/:id" component={PrescreenDetail} />
      <Route path="/qa/active" component={ActiveTaskList} />
      <Route path="/qa/active/:id" component={ActiveTaskDetail} />
      {/* SPEC-UnifiedQADetail Phase 2 — agent list survives. The agent detail
          page is unified onto /qa/delivery/:id; legacy bookmarks get a
          one-cycle <Redirect> below. */}
      <Route path="/qa/agent" component={AgentList} />
      <Route path="/qa/agent/delivery/:id">
        {(params) => <Redirect to={`/qa/delivery/${params.id}`} />}
      </Route>
      <Route path="/qa/delivery/:id" component={QaDetail} />
      {/* Session 11 — all-user preferences (per-agent toast suppression). Not admin-gated. */}
      <Route path="/settings" component={NotificationSettings} />
      <Route path="/admin/users">{() => <AdminRoute component={AdminUsers} />}</Route>
      <Route path="/admin/settings">{() => <AdminRoute component={AdminSettings} />}</Route>
      <Route path="/admin/annotation-review">{() => <AdminRoute component={AnnotationReview} />}</Route>
      {/* Model Audit Mode — Sarah's per-delivery model grading queue. */}
      <Route path="/model/audit">{() => <AdminRoute component={ModelAudit} />}</Route>
      <Route path="/model/cohorts" component={BaselineManager} />
      {/* SPEC-TrainingSimulator — Training Set / key editor (Phase A). The
          trainee runner at /train lands in Phase B. */}
      <Route path="/train/manage">{() => <SeniorOrAdminRoute component={TrainManage} />}</Route>
      <Route path="/train/manage/item/:id">{() => <SeniorOrAdminRoute component={TrainManageItem} />}</Route>
      {/* SPEC-TrainingSimulator Phase B — trainee runner (§8). Trainees are
          bounced here from every other route (traineeRedirectTarget). */}
      <Route path="/train">{() => <TraineeRoute component={TrainHome} />}</Route>
      <Route path="/train/lesson/:id">{() => <TraineeRoute component={TrainLesson} />}</Route>
      <Route path="/train/run/:attemptId">{() => <TraineeRoute component={TrainRun} />}</Route>
      <Route path="/train/preview/:itemId">{() => <SeniorOrAdminRoute component={TrainRun} />}</Route>
      <Route component={NotFound} />
    </Switch>
  );
}

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
        <p className="text-muted-foreground">Loading...</p>
      </div>
    </div>
  );
}

function NotApprovedScreen({ user, onLogout }: { user: { email?: string | null }; onLogout: () => void }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-2">
            <AlertCircle className="w-6 h-6 text-destructive" />
          </div>
          <CardTitle>Access Pending</CardTitle>
          <CardDescription>
            Your account is not yet approved to access this platform
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-muted rounded-md p-3 text-center">
            <p className="text-sm text-muted-foreground">Signed in as</p>
            <p className="font-medium">{user.email ?? "Unknown"}</p>
          </div>
          <p className="text-sm text-muted-foreground text-center">
            Contact your administrator to request access to the Starling platform.
          </p>
          <Button
            variant="outline"
            className="w-full"
            onClick={() => onLogout()}
            data-testid="button-logout"
          >
            Sign Out
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Fires a PostHog pageview on every Wouter route change.
 * Must live inside the Router so `useLocation` is available.
 */
function PostHogPageview() {
  const [location] = useLocation();
  useEffect(() => {
    posthog.capture("$pageview", { $current_url: window.location.href });
  }, [location]);
  return null;
}

function AuthenticatedApp() {
  // SPEC-AdminImpersonation §"Architectural cleanup" — `useAuth()` and the
  // approval probe now share a SINGLE query (key `AUTH_QUERY_KEY` =
  // `["/api/auth/check-approved"]`, `staleTime: 0`). `approval` is the
  // raw payload; `user` is synthesized from it. No second `useQuery` call.
  const { user, logout, approval, isLoading: isCheckingApproval } = useAuth();
  const [location] = useLocation();

  // PostHog — identify the user as soon as we know who they are.
  // Must come before any conditional returns (hooks rules).
  // We use `user.id` as the distinct ID so replays are tied to the
  // actual account. The effect re-runs only when the user ID changes
  // (e.g. impersonation swap), not on every render.
  useEffect(() => {
    if (user?.id) {
      posthog.identify(user.id, {
        email: user.email ?? undefined,
        name: user.name ?? undefined,
        role: user.role ?? undefined,
      });
    }
  }, [user?.id]);

  if (isCheckingApproval) {
    return <LoadingScreen />;
  }

  if (!approval?.approved) {
    return <NotApprovedScreen user={user || {}} onLogout={logout} />;
  }

  const style = {
    "--sidebar-width": "16rem",
    "--sidebar-width-icon": "3rem",
  };

  // SPEC-Auth-Users-Roles-Audit — RoleContext value derived from the new
  // payload shape. `deriveRoleValue` is locked by `lib/__tests__/role-helpers.test.ts`.
  // SPEC-AdminImpersonation — sourced from the same `useAuth()` payload so
  // the banner (which reads `useAuth().effectiveRole`) and route guards
  // (which read `useRole().role`) can never desync.
  const roleValue = deriveRoleValue(approval);

  const isQaListPage = location === "/" || location === "/qa/list";

  // SPEC-TrainingSimulator §3 — a trainee is served /train and its sub-routes only.
  // Checked here, above the Switch, so it covers every route including ones
  // added later. The role is known at this point (approval already resolved).
  const traineeBounce = traineeRedirectTarget(roleValue.role, location);
  const isTrainee = roleValue.role === "trainee";

  return (
    <RoleContext.Provider value={roleValue}>
      {/* SPEC-AdminImpersonation §"Client wiring #4" — banner mounts ABOVE
          the existing app shell so its h-10 push shifts every page below it
          consistently. AgentClaimSubscriptionProvider's toasts still surface
          via React Portal so the banner doesn't collide with them. */}
      <PostHogPageview />
      <ImpersonationBanner />
      <ImpersonationErrorListener />
      <AgentClaimSubscriptionProvider>
      <SidebarProvider defaultOpen={false} style={style as React.CSSProperties}>
        <div className="flex h-screen w-full">
          {/* SPEC-ActiveTask B2 — global early-delivery toast for every user
              except trainees (§8.5: no socket, no live-QA fetch for them). */}
          {!isTrainee && <EarlyRiskNotifier />}
          {/* SPEC-OverrideNotification — global override toast for Agents. */}
          {!isTrainee && <OverrideNotificationHandler />}
          <AppSidebar />
          <div className="flex flex-col flex-1 overflow-hidden">
            <header className="flex items-center justify-between gap-2 p-3 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
              <div className="flex items-center gap-3">
                <SidebarTrigger data-testid="button-sidebar-toggle" />
                {/* SPEC-UnifiedQADetail Phase 2 — header logo click target
                    follows viewMode: agent view → /qa/agent, senior view →
                    /qa/list. */}
                <HeaderLockup />
                {isQaListPage && (
                  <div className="flex flex-col" data-testid="header-page-title">
                    <span style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.2, color: Starling.purple, letterSpacing: "-0.01em" }}>Delivery QA</span>
                    <span className="text-muted-foreground" style={{ fontSize: 12, lineHeight: 1.2 }}>Review and verify completed deliveries</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                {/* SPEC-UnifiedQADetail Phase 2 — header view-mode toggle.
                    Hidden for real agents (canToggle === false). */}
                {!isTrainee && <ViewModeToggle />}
                {/* Theme and Sign out are in the app sidebar (app-sidebar.tsx). */}
                <span className="text-sm text-muted-foreground hidden sm:block">
                  {user?.email}
                </span>
              </div>
            </header>
            <main className="flex-1 overflow-auto">
              {/* A trainee off /train gets ONLY the redirect — the live pages
                  never mount, not even for one render (their queries would
                  403 and the claim/socket code must never run for a trainee). */}
              {traineeBounce ? <Redirect to={traineeBounce} /> : <Router />}
            </main>
          </div>
        </div>
      </SidebarProvider>
      </AgentClaimSubscriptionProvider>
    </RoleContext.Provider>
  );
}

function AppContent() {
  const { user, isLoading } = useAuth();
  if (isLoading) {
    return <LoadingScreen />;
  }
  if (!user) {
    return <LandingPage />;
  }
  return <AuthenticatedApp />;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="light" storageKey="delivery-ui-theme">
        <TooltipProvider>
          <AppContent />
          <Toaster />
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;

export { shouldRedirectFromAdminRoute };
